import {
  Alert,
  Button,
  CloseButton,
  Flex,
  Heading,
  IconAddLine,
  Modal,
  Spinner,
  Text,
  TextInput,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useContext, useEffect, useRef, useState } from "react";
import { MasteryLevelService } from "../../../services/MasteryLevelService";
import { MasteryLevelContext } from "../../../utils/Context";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { Selector } from "../../Selectors/Selector";
import RatingLevel from "./RatingLevel";

const MapRatingsToMasteryModal = ({
  open,
  setOpen,
  handleClose,
  ratingScale,
  updateMappings,
}) => {
  const showNotification = useNotification();
  const { masteryLevels } = useContext(MasteryLevelContext);
  const [loading, isLoading] = useState(false);
  const [errMessage, setErrMessage] = useState("");

  const nameRef = useRef();
  const [ratingLevels, setRatingLevels] = useState([]);
  const [masteryAt, setMasteryAt] = useState("");

  useEffect(() => {
    if (ratingScale && open) {
      setRatingLevels(
        ratingScale.levels.map((level) => ({
          ...level,
          points: Number(level.points),
        }))
      );
      setMasteryAt(ratingScale.mastery_points.toString());
    }
  }, [ratingScale, open]);

  useEffect(() => {
    if (open) {
      const ratingPoints = ratingLevels.map((item) => item.points);
      if (!ratingPoints.includes(Number(masteryAt))) {
        setMasteryAt("");
      }
    }
  }, [ratingLevels]);

  const handleSubmit = async () => {
    setErrMessage("");
    const masteryRatingLevels = ratingLevels.map((level, index) => ({
      ...level,
      points: level.points.toString(),
      seq: index,
    }));
    const masteryLevelMappings = {
      id: ratingScale?.id,
      title: nameRef.current.value,
      levels: masteryRatingLevels,
      mastery_points: parseInt(masteryAt),
      isDefault: ratingScale?.isDefault,
      is_canvas_default: ratingScale?.is_canvas_default,
      is_default: ratingScale?.is_default,
      is_mapped: ratingScale?.is_mapped,
    };
    isLoading(true);
    await MasteryLevelService.createOrUpdateRatingToMasteryLevelMap(
      masteryLevelMappings
    )
      .then(() => {
        setOpen(false);
        updateMappings(true);
        showNotification(
          t("notifications.general.updateSuccess", { obj: "Rating Scale" })
        );
      })
      .catch(() => {
        showNotification(
          t("notifications.general.updateError", { obj: "Rating Scale" }),
          ERROR
        );
      })
      .finally(() => isLoading(false));
  };

  const missingMasteryLevelDetails = () => {
    let missing = false;
    ratingLevels.forEach((item) => {
      if (item.points === "" || !item.mastery_level_id || !item.description) {
        missing = true;
      }
    });
    return missing;
  };

  const duplicateRatingPoints = () => {
    const points = ratingLevels.map((item) => item.points);
    return points.length !== new Set(points).size;
  };

  const missingRequiredFields = () => {
    let missing = false;
    if (!nameRef.current.value) {
      setErrMessage(t("masteryLevels.nameRequired"));
      missing = true;
    } else if (ratingLevels.length === 0) {
      setErrMessage(t("masteryLevels.noLevelsError"));
      missing = true;
    } else if (missingMasteryLevelDetails()) {
      setErrMessage(t("masteryLevels.levelsRequired"));
      missing = true;
    } else if (duplicateRatingPoints()) {
      setErrMessage(t("masteryLevels.pointsMustBeUnique"));
      missing = true;
    } else if (masteryAt === "") {
      setErrMessage(t("masteryLevels.masteryAtRequired"));
      missing = true;
    }
    return missing;
  };

  const handleAdd = () => {
    setRatingLevels((prevLevels) => [
      ...prevLevels,
      { points: "", mastery_level_id: masteryLevels[0].id, description: "" },
    ]);
  };

  const masteryAtOptions = () => {
    const points = ratingLevels.map((item) => item.points);
    const options = [...new Set(points)];
    return options.map((point) => ({
      id: `${point}`,
      label: `${point} Points`,
    }));
  };

  return (
    <Modal
      open={open}
      onDismiss={handleClose}
      onExited={() => {
        setErrMessage("");
        setRatingLevels(ratingScale?.levels ?? []);
        setMasteryAt((ratingScale?.mastery_points ?? "").toString());
      }}
      size="large"
      label={
        ratingScale
          ? t("masteryLevels.editRatingScale")
          : t("masteryLevels.createRatingScale")
      }
      shouldCloseOnDocumentClick
    >
      <Modal.Header>
        <Flex>
          <Flex.Item shouldGrow>
            <Heading level="h2" as="h1">
              {ratingScale
                ? t("masteryLevels.editRatingScale")
                : t("masteryLevels.createRatingScale")}
            </Heading>
          </Flex.Item>
          <Flex.Item>
            <CloseButton
              size="small"
              screenReaderLabel={t("global.close")}
              onClick={handleClose}
            />
          </Flex.Item>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        {errMessage && (
          <Alert variant="warning" margin="0 0 medium 0">
            {errMessage}
          </Alert>
        )}
        <Flex gap="medium">
          <Flex.Item shouldGrow>
            <TextInput
              id="mastery-levels-name"
              ref={nameRef}
              interaction={
                ratingScale?.is_canvas_default ? "disabled" : "enabled"
              }
              renderLabel={t("masteryLevels.mappingName")}
              defaultValue={ratingScale?.title}
            />
          </Flex.Item>
          <Flex.Item align="end">
            <Button
              onClick={() => handleAdd()}
              renderIcon={IconAddLine}
              interaction={
                ratingScale?.is_canvas_default ||
                (ratingScale && !ratingScale?.is_editable)
                  ? "disabled"
                  : "enabled"
              }
            >
              {t("masteryLevels.addLevel")}
            </Button>
          </Flex.Item>
        </Flex>
        {ratingLevels.map((level, key) => (
          <View as="div" margin="medium 0" key={key}>
            <RatingLevel
              key={key}
              index={key}
              ratingLevel={level}
              setRatingLevels={setRatingLevels}
              isCanvasDefault={ratingScale?.is_canvas_default}
              isEditable={!ratingScale || ratingScale?.is_editable}
              isRatingScaleMapped={ratingScale?.is_mapped}
            />
          </View>
        ))}
        <Flex alignItems="start" padding="small 0">
          <Flex.Item padding="0 small 0 0">
            {ratingScale?.is_canvas_default ||
            ratingScale?.is_editable === false ? (
              <Text size="medium">
                {masteryAt === ""
                  ? t("masteryLevels.noMastery")
                  : t("masteryLevels.masteryAt") + " " + masteryAt}
              </Text>
            ) : (
              <Selector
                id="level-at-selector"
                label={t("masteryLevels.masteryAt")}
                options={masteryAtOptions()}
                selectHandler={(value) => setMasteryAt(value)}
                selectedItemId={masteryAt}
                clear={masteryAt === ""}
              />
            )}
          </Flex.Item>
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Button margin="0 small" onClick={handleClose}>
          {t("global.cancel")}
        </Button>
        <Button
          color="primary"
          type="submit"
          onClick={() => {
            if (!missingRequiredFields()) {
              handleSubmit();
            }
          }}
          disabled={loading}
        >
          {loading ? (
            <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
          ) : (
            t("global.submit")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MapRatingsToMasteryModal;
